'use client';

import React, { useMemo, useCallback, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import type {
  ColDef,
  GridReadyEvent,
  SelectionChangedEvent,
  RowClickedEvent,
  GridApi,
} from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry, themeQuartz } from 'ag-grid-community';
import { cn } from '@/lib/utils';

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule]);

export interface AGGridTableProps<T> {
  /** Row data to display */
  data: T[];
  /** Column definitions */
  columns: ColDef<T>[];
  /** Enable row selection */
  selectable?: boolean | undefined;
  /** Selection mode */
  selectionMode?: 'single' | 'multiple' | undefined;
  /** Callback when selection changes */
  onSelectionChange?: ((selectedRows: T[]) => void) | undefined;
  /** Callback when row is clicked */
  onRowClick?: ((row: T) => void) | undefined;
  /** Enable pagination */
  pagination?: boolean | undefined;
  /** Page size options */
  pageSizeOptions?: number[] | undefined;
  /** Default page size */
  defaultPageSize?: number | undefined;
  /** Enable global search/filter */
  searchable?: boolean | undefined;
  /** Search placeholder */
  searchPlaceholder?: string | undefined;
  /** When provided, the search box calls this instead of AG Grid's own
   * client-side quickFilterText — use for server-side search across a
   * paginated dataset larger than what's currently loaded. */
  onSearchChange?: ((value: string) => void) | undefined;
  /** Loading state */
  loading?: boolean | undefined;
  /** Empty state message */
  emptyMessage?: string | undefined;
  /** Table height */
  height?: string | number | undefined;
  /** Additional class name */
  className?: string | undefined;
  /** Enable column resizing */
  resizable?: boolean | undefined;
  /** Enable column sorting */
  sortable?: boolean | undefined;
  /** Suppress row click selection */
  suppressRowClickSelection?: boolean | undefined;
  /** Auto size columns on load */
  autoSizeColumns?: boolean | undefined;
  /** Row height */
  rowHeight?: number | undefined;
  /** Header height */
  headerHeight?: number | undefined;
  /** Get row ID for selection persistence */
  getRowId?: ((data: T) => string) | undefined;
  /** Dark mode */
  darkMode?: boolean | undefined;
}

/**
 * AG Grid Table Component
 *
 * A feature-rich data table built on AG Grid Community Edition.
 * Provides sorting, filtering, pagination, selection, and more.
 */
export function AGGridTable<T extends object>({
  data,
  columns,
  selectable = false,
  selectionMode = 'multiple',
  onSelectionChange,
  onRowClick,
  pagination = true,
  pageSizeOptions = [10, 25, 50, 100],
  defaultPageSize = 25,
  searchable = true,
  searchPlaceholder = 'Search...',
  onSearchChange,
  loading = false,
  emptyMessage = 'No data available',
  height = 600,
  className,
  resizable = true,
  sortable = true,
  suppressRowClickSelection = true,
  autoSizeColumns = false,
  rowHeight = 48,
  headerHeight = 48,
  getRowId,
  darkMode = false,
}: AGGridTableProps<T>) {
  const gridRef = useRef<AgGridReact<T>>(null);
  const [searchText, setSearchText] = useState('');
  const [gridApi, setGridApi] = useState<GridApi<T> | null>(null);

  // Create custom theme based on Quartz
  const customTheme = useMemo(() => {
    return themeQuartz.withParams({
      accentColor: '#3b82f6',
      backgroundColor: darkMode ? '#1f2937' : '#ffffff',
      foregroundColor: darkMode ? '#f3f4f6' : '#111827',
      borderColor: darkMode ? '#374151' : '#e5e7eb',
      headerBackgroundColor: darkMode ? '#111827' : '#f9fafb',
      headerTextColor: darkMode ? '#f3f4f6' : '#374151',
      oddRowBackgroundColor: darkMode ? '#1f2937' : '#ffffff',
      rowHoverColor: darkMode ? '#374151' : '#f3f4f6',
      selectedRowBackgroundColor: darkMode ? '#1e3a5f' : '#dbeafe',
      fontFamily: 'inherit',
      fontSize: 14,
      headerFontSize: 13,
      headerFontWeight: 600,
      cellHorizontalPadding: 16,
      rowBorder: true,
      wrapperBorder: true,
      wrapperBorderRadius: 8,
    });
  }, [darkMode]);

  // Default column properties
  const defaultColDef = useMemo<ColDef<T>>(() => ({
    sortable,
    resizable,
    filter: true,
    filterParams: {
      buttons: ['reset', 'apply'],
    },
    minWidth: 100,
    flex: 1,
  }), [sortable, resizable]);

  // Process columns to ensure proper configuration
  const processedColumns = useMemo(() => {
    return columns.map(col => ({
      ...col,
      sortable: col.sortable ?? sortable,
      resizable: col.resizable ?? resizable,
    }));
  }, [columns, sortable, resizable]);

  // Handle grid ready
  const onGridReady = useCallback((params: GridReadyEvent<T>) => {
    setGridApi(params.api);

    if (autoSizeColumns) {
      params.api.autoSizeAllColumns();
    }
  }, [autoSizeColumns]);

  // Handle selection change
  const handleSelectionChanged = useCallback((event: SelectionChangedEvent<T>) => {
    if (onSelectionChange) {
      const selectedRows = event.api.getSelectedRows();
      onSelectionChange(selectedRows);
    }
  }, [onSelectionChange]);

  // Handle row click
  const handleRowClicked = useCallback((event: RowClickedEvent<T>) => {
    if (onRowClick && event.data) {
      onRowClick(event.data);
    }
  }, [onRowClick]);

  // Handle search
  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    if (onSearchChange) {
      onSearchChange(value);
      return;
    }
    if (gridApi) {
      gridApi.setGridOption('quickFilterText', value);
    }
  }, [gridApi, onSearchChange]);

  // Get row ID for selection
  const getRowIdCallback = useCallback((params: { data: T }) => {
    if (getRowId) {
      return getRowId(params.data);
    }
    // Try to use common ID fields
    const data = params.data as Record<string, unknown>;
    return String(data.id || data._id || data.uuid || JSON.stringify(data));
  }, [getRowId]);

  // Loading overlay component
  const loadingOverlayComponent = useMemo(() => {
    return () => (
      <div className="flex items-center justify-center h-full">
        <div className="flex items-center gap-3">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          <span className="text-text-secondary">Loading...</span>
        </div>
      </div>
    );
  }, []);

  // No rows overlay component
  const noRowsOverlayComponent = useMemo(() => {
    return () => (
      <div className="flex flex-col items-center justify-center h-full py-12">
        <svg
          className="w-12 h-12 text-gray-300 mb-4"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4"
          />
        </svg>
        <p className="text-text-tertiary text-sm">{emptyMessage}</p>
      </div>
    );
  }, [emptyMessage]);

  return (
    <div className={cn('flex flex-col gap-4', className)}>
      {/* Toolbar */}
      {searchable && (
        <div className="flex items-center justify-between">
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
            <input
              type="text"
              placeholder={searchPlaceholder}
              value={searchText}
              onChange={handleSearchChange}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 w-64"
            />
          </div>
        </div>
      )}

      {/* AG Grid */}
      <div
        style={{ height: typeof height === 'number' ? `${height}px` : height }}
        className="w-full"
      >
        <AgGridReact<T>
          ref={gridRef}
          theme={customTheme}
          rowData={data}
          columnDefs={processedColumns}
          defaultColDef={defaultColDef}
          onGridReady={onGridReady}
          // Selection
          {...(selectable ? { rowSelection: selectionMode === 'multiple' ? 'multiple' : 'single' } : {})}
          suppressRowClickSelection={suppressRowClickSelection}
          onSelectionChanged={handleSelectionChanged}
          onRowClicked={handleRowClicked}
          // Pagination
          pagination={pagination}
          paginationPageSize={defaultPageSize}
          paginationPageSizeSelector={pageSizeOptions}
          // Sizing
          rowHeight={rowHeight}
          headerHeight={headerHeight}
          // Loading states
          loading={loading}
          loadingOverlayComponent={loadingOverlayComponent}
          noRowsOverlayComponent={noRowsOverlayComponent}
          // Other options
          getRowId={getRowIdCallback}
          animateRows={true}
          enableCellTextSelection={true}
          suppressCellFocus={true}
          suppressMovableColumns={false}
          // Accessibility
          suppressMenuHide={true}
        />
      </div>
    </div>
  );
}

export default AGGridTable;
